import { TextField, Typography, Box, IconButton } from '@mui/material';
import { contractPageSelectors, getSelectorAttribute } from '@vy/app-selectors';
import { VyIcon } from '@vy/ui-design-system';
import type { Customer, Plan } from '@vy/types';
import { isNotNullOrUndefined } from '@vy/utils';
import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useSoftUpdatePlan } from '../../../../../hooks/requests/use-update-soft-plan';
import { useActivate } from '../../../../../hooks/use-activate';
import { runMutation } from '../../../../../utils/vy-mutation';

type ContractPageTitleProps = {
  plan: Plan;
  customer?: Customer;
};

export const ContractPageTitle: React.FC<ContractPageTitleProps> = ({ plan, customer }) => {
  const [isEditing, enterEditMode, exitEditMode] = useActivate(false);
  const [editPlanName, setEditPlanName] = useState(plan.name);
  const [isHovered, setIsHovered] = useState(false);

  const softUpdatePlanMutation = useSoftUpdatePlan(plan.id);

  const handlePlanNameBlur = async (event: React.FocusEvent<HTMLInputElement>) => {
    if (event.target.value === plan.name) {
      return;
    }

    await runMutation(
      softUpdatePlanMutation,
      { name: event.target.value },
      {
        pending: 'Updating contract...',
        success: 'Contract updated',
        error: 'Failed to update contract',
      },
      {
        onSuccess: () => {
          exitEditMode();
          setEditPlanName(event.target.value);
        },
      },
    );
  };

  const handleTitleClick = () => {
    enterEditMode();
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditPlanName(event.target.value);
  };

  return (
    <Box
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        display: "flex",
        alignItems: "center"
      }}>
      {isNotNullOrUndefined(customer) && (
        <NavLink to={`/customers/${customer.id}/overview`}>
          <Typography
            variant="h5"
            sx={{
              color: 'text.primary',
              fontWeight: 400,
              fontSize: '1.5rem',
              lineHeight: '32.02px',
              textTransform: 'capitalize',
              marginRight: '10px',
              display: 'inline-flex',
              pointerEvents: '',
            }}
          >
            {`${customer.name} -`}
          </Typography>
        </NavLink>
      )}
      {isEditing ? (
        <TextField
          value={editPlanName}
          onChange={handleChange}
          onBlur={handlePlanNameBlur}
          autoFocus
          variant="standard"
          size="small"
          sx={{
            '& .MuiInputBase-input': {
              fontWeight: 400,
              fontSize: '1.5rem',
              lineHeight: '32.02px',
              textTransform: 'capitalize',
              padding: 0,
              width: `${editPlanName.length}ch`,
            },
          }}
        />
      ) : (
        <Typography
          onClick={handleTitleClick}
          variant="h5"
          sx={{
            color: 'text.primary',
            cursor: 'pointer',
            fontWeight: 400,
            fontSize: '1.5rem',
            lineHeight: '32.02px',
            textTransform: 'capitalize',
          }}
        >
          {editPlanName}
        </Typography>
      )}
      {isHovered && !isEditing && (
        <IconButton
          size="small"
          onClick={handleTitleClick}
          sx={{ marginLeft: '10px', height: '24px' }}
          {...getSelectorAttribute(contractPageSelectors.header.editPlanNameButton)}
        >
          <VyIcon icon="Edit" />
        </IconButton>
      )}
    </Box>
  );
};
